import React, { useMemo, useState } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Settings,
  Trophy,
  History,
  Gamepad2,
  Users,
  CreditCard,
  Medal,
  LogOut,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';

const css = `
@media (max-width: 768px) {
  .al-sidebar {
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }
  .al-sidebar.al-open {
    transform: translateX(0);
  }
  .al-content {
    margin-left: 0 !important;
    padding-top: 60px !important;
  }
  .al-overlay {
    display: block !important;
  }
}
@media (min-width: 769px) {
  .al-hamburger { display: none !important; }
}
`;

const S = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
  },
  sidebar: {
    width: '240px',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRight: '1px solid rgba(212, 175, 55, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 200,
  },
  overlay: {
    display: 'none',
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 199,
  },
  hamburger: {
    position: 'fixed',
    top: '12px',
    left: '12px',
    zIndex: 210,
    background: 'var(--color-bg-secondary)',
    border: '1px solid rgba(212,175,55,0.15)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-primary)',
    padding: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '44px',
    minHeight: '44px',
  },
  brand: {
    padding: 'var(--space-lg) var(--space-lg)',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  brandText: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.2rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textDecoration: 'none',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    display: 'block',
  },
  brandSub: {
    fontSize: '0.7rem',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginTop: '2px',
  },
  menu: {
    flex: 1,
    padding: 'var(--space-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflowY: 'auto',
  },
  menuLabel: {
    fontSize: '0.65rem',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    padding: '12px 12px 6px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-secondary)',
    textDecoration: 'none',
    fontSize: '0.88rem',
    fontWeight: 500,
    transition: 'all var(--transition-fast)',
    minHeight: '44px',
  },
  menuItemActive: {
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    color: 'var(--color-primary)',
  },
  footer: {
    padding: 'var(--space-md) var(--space-lg)',
    borderTop: '1px solid rgba(212, 175, 55, 0.1)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
  },
  userRole: {
    fontSize: '0.7rem',
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  content: {
    flex: 1,
    marginLeft: '240px',
    padding: 'var(--space-lg)',
    minHeight: '100vh',
    minWidth: 0,
  },
};

export default function AdminLayout() {
  const { user, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = useMemo(() => {
    const items = [
      { label: 'Panoramica', path: '/admin', icon: LayoutDashboard, end: true },
    ];

    if (isAdmin) {
      items.push({ label: 'Organizzatori', path: '/admin/users', icon: Users });
    }

    items.push(
      { label: 'Le mie partite', path: '/admin/games', icon: History },
      { label: 'Classifica', path: '/admin/leaderboard', icon: Medal },
      { label: 'I miei premi', path: '/admin/prizes', icon: Trophy },
      { label: 'Impostazioni', path: '/admin/settings', icon: Settings },
      { label: 'Abbonamento', path: '/admin/subscription', icon: CreditCard },
    );

    return items;
  }, [isAdmin]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div style={S.layout}>
      <style>{css}</style>

      {/* Hamburger button (mobile only) */}
      <button
        className="al-hamburger"
        style={S.hamburger}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Overlay (mobile only) */}
      {sidebarOpen && (
        <div className="al-overlay" style={{ ...S.overlay, display: 'block' }} onClick={closeSidebar} />
      )}

      <aside className={`al-sidebar ${sidebarOpen ? 'al-open' : ''}`} style={S.sidebar}>
        <div style={S.brand}>
          <Link to="/" style={S.brandText}>PickBoxGame</Link>
          <div style={S.brandSub}>
            {isAdmin ? 'Amministrazione' : 'Gestione eventi'}
          </div>
        </div>

        <nav style={S.menu}>
          <div style={S.menuLabel}>Gestione</div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={closeSidebar}
                style={({ isActive }) => ({
                  ...S.menuItem,
                  ...(isActive ? S.menuItemActive : {}),
                })}
              >
                {({ isActive }) => (
                  <>
                    <Icon size={18} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {isActive && <ChevronRight size={14} />}
                  </>
                )}
              </NavLink>
            );
          })}

          <div style={{ ...S.menuLabel, marginTop: 'var(--space-md)' }}>Operazioni</div>
          <NavLink
            to="/control"
            onClick={closeSidebar}
            style={({ isActive }) => ({
              ...S.menuItem,
              ...(isActive ? S.menuItemActive : {}),
            })}
          >
            <Gamepad2 size={18} />
            <span style={{ flex: 1 }}>Partita</span>
          </NavLink>
        </nav>

        <div style={S.footer}>
          <div style={S.userInfo}>
            <div>
              <div style={S.userName}>{user?.name || 'Organizzatore'}</div>
              <div style={S.userRole}>
                {user?.role === 'admin' ? 'Amministratore' : 'Organizzatore'}
              </div>
            </div>
            <button
              onClick={logout}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                padding: '10px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                minWidth: '44px',
                minHeight: '44px',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color var(--transition-fast)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-danger)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
              title="Esci"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <main className="al-content" style={S.content}>
        <Outlet />
      </main>
    </div>
  );
}
